/**
 * Live agent run console.
 *
 * Replays a scripted pipeline against a wall clock so the workspace shows the
 * work being done — which agent is active, what it found, and how confident it
 * is — instead of teleporting to a finished result. Each log line is stamped
 * and colour-coded; warnings are the ones an evaluator must act on.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { theme } from '../../styles/theme';
import { Check, Loader2, CircleDashed, AlertTriangle, Sparkles, Minimize2 } from 'lucide-react';
import type { AgentDef, AgentLine } from '../../data/evaluationMocks';

const spin = keyframes`to { transform: rotate(360deg) }`;
const blink = keyframes`0%,100%{opacity:1}50%{opacity:.25}`;

const Wrap = styled.div`
  background: ${theme.colors.surface};
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
`;

const Head = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-weight: 700; font-size: 14px; color: ${theme.colors.textPrimary};
  display: flex; align-items: center; gap: 8px;
`;

const Sub = styled.div`
  font-size: 11.5px; color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const Right = styled.div`margin-left: auto; display: flex; align-items: center; gap: ${theme.spacing[4]};`;

const Clock = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 12px; color: ${theme.colors.primary[500]}; font-variant-numeric: tabular-nums; font-weight: 600;
`;

const SkipBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: ${theme.colors.surface}; border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary}; border-radius: 8px;
  padding: 5px 10px; font-size: 11.5px; font-weight: 600; cursor: pointer;
  transition: all 150ms ease;
  &:hover { border-color: ${theme.colors.primary[300]}; color: ${theme.colors.primary[500]}; }
`;

const Bar = styled.div`height: 2px; background: ${theme.colors.backgroundTertiary};`;
const Fill = styled.div<{ $p: number }>`
  height: 100%; width: ${p => p.$p}%;
  background: ${theme.colors.primary[500]};
  transition: width 180ms linear;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Agents = styled.div`
  padding: ${theme.spacing[4]};
  border-right: 1px solid ${theme.colors.border};
  display: flex; flex-direction: column; gap: 2px;
  @media (max-width: 900px) { border-right: 0; border-bottom: 1px solid ${theme.colors.border}; }
`;

const AgentRow = styled.div<{ $state: 'done' | 'run' | 'wait' }>`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 9px 10px; border-radius: 9px;
  background: ${p => p.$state === 'run' ? theme.colors.primary[50] : 'transparent'};
  transition: background 200ms ease;
`;

const AgentIcon = styled.span<{ $state: 'done' | 'run' | 'wait' }>`
  flex: 0 0 auto; margin-top: 1px;
  color: ${p => p.$state === 'done' ? theme.colors.success[600] : p.$state === 'run' ? theme.colors.primary[500] : theme.colors.textDisabled};
  display: grid; place-items: center;
  ${p => p.$state === 'run' && css`svg { animation: ${spin} 1s linear infinite; }`}
`;

const AgentName = styled.div<{ $state: 'done' | 'run' | 'wait' }>`
  font-size: 12.5px; font-weight: 600;
  color: ${p => p.$state === 'wait' ? theme.colors.textDisabled : theme.colors.textPrimary};
`;

const AgentDetail = styled.div`font-size: 11px; color: ${theme.colors.textMuted}; margin-top: 1px; line-height: 1.4;`;

const Log = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  height: 290px;
  overflow-y: auto;
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11.5px;
  line-height: 1.85;
  scroll-behavior: smooth;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.borderHover}; border-radius: 3px; }
`;

const Line = styled.div<{ $kind?: string }>`
  display: flex; gap: 12px;
  color: ${p => p.$kind === 'warn' ? theme.colors.warning[700] : p.$kind === 'ok' ? theme.colors.success[700] : theme.colors.textSecondary};
  animation: ${keyframes`from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}`} 220ms ease;
`;

const Stamp = styled.span`color: ${theme.colors.textDisabled}; flex: 0 0 auto; font-variant-numeric: tabular-nums;`;
const Caret = styled.span`animation: ${blink} 1s steps(1) infinite; color: ${theme.colors.primary[400]};`;

interface Props {
  title: string;
  subtitle?: string;
  agents: AgentDef[];
  log: AgentLine[];
  /** Playback multiplier. >1 runs faster than the scripted clock. */
  speed?: number;
  onComplete?: () => void;
}

const AgentRunConsole: React.FC<Props> = ({ title, subtitle, agents, log, speed = 1, onComplete }) => {
  const duration = agents[agents.length - 1]?.until ?? 10;
  const [elapsed, setElapsed] = useState(0);
  const logRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const e = ((performance.now() - started) / 1000) * speed;
      setElapsed(e);
      if (e < duration + 0.4) {
        raf = requestAnimationFrame(tick);
      } else if (!doneRef.current) {
        doneRef.current = true;
        onComplete?.();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => log.filter(l => l.t <= elapsed), [log, elapsed]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [visible.length]);

  /* There is no way to fast-forward real computation. This detaches from the
     live view the way a long job would, and surfaces the finished result. */
  const skip = () => {
    setElapsed(duration + 1);
    if (!doneRef.current) { doneRef.current = true; onComplete?.(); }
  };

  const progress = Math.min(100, (elapsed / duration) * 100);
  const stateOf = (a: AgentDef, i: number): 'done' | 'run' | 'wait' => {
    const prev = i === 0 ? 0 : agents[i - 1].until;
    if (elapsed >= a.until) return 'done';
    if (elapsed >= prev) return 'run';
    return 'wait';
  };

  return (
    <Wrap>
      <Head>
        <div>
          <Title><Sparkles size={15} /> {title}</Title>
          {subtitle && <Sub>{subtitle}</Sub>}
        </div>
        <Right>
          <Clock>{Math.min(elapsed, duration).toFixed(1)}s</Clock>
          {progress < 100 && (
            <SkipBtn onClick={skip} title="Continue the run without watching it">
              <Minimize2 size={11} /> Run in background
            </SkipBtn>
          )}
        </Right>
      </Head>
      <Bar><Fill $p={progress} /></Bar>
      <Body>
        <Agents>
          {agents.map((a, i) => {
            const st = stateOf(a, i);
            return (
              <AgentRow key={a.id} $state={st}>
                <AgentIcon $state={st}>
                  {st === 'done' ? <Check size={14} strokeWidth={3} /> : st === 'run' ? <Loader2 size={14} /> : <CircleDashed size={14} />}
                </AgentIcon>
                <div>
                  <AgentName $state={st}>{a.name}</AgentName>
                  <AgentDetail>{a.detail}</AgentDetail>
                </div>
              </AgentRow>
            );
          })}
        </Agents>
        <Log ref={logRef}>
          {visible.map((l, i) => (
            <Line key={i} $kind={l.kind}>
              <Stamp>{l.t.toFixed(1).padStart(4, '0')}s</Stamp>
              <span>
                {l.kind === 'warn' && <AlertTriangle size={10} style={{ marginRight: 5, verticalAlign: 'middle' }} />}
                {l.text}
              </span>
            </Line>
          ))}
          {progress < 100 && <Line><Stamp>{elapsed.toFixed(1).padStart(4, '0')}s</Stamp><Caret>▊</Caret></Line>}
        </Log>
      </Body>
    </Wrap>
  );
};

export default AgentRunConsole;
